import { CityDetails, LoginDetails } from "./interface";

enum STORAGE_VAR {
  CITY_DETAILS = "city_details",
  SELECT_TIME = "select_time",
  LOGIN_DETAILS = "login_details",
  BIKE_SEARCH_DATA = "bikeSearchData",
  COMPARE_LIST_KEY = 'compareList'
}

/**
 * Safely set item in localStorage
 */
export const safeSetItem = (key: string, value: unknown): void => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.setItem(key, JSON.stringify(value));
    }
  } catch (error) {
    console.error(`Error setting localStorage item ${key}:`, error);
  }
};

/**
 * Safely get item from localStorage
 */
export const safeGetItem = (key: string): unknown => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    }
    return null;
  } catch (error) {
    console.error(`Error getting localStorage item ${key}:`, error);
    return null;
  }
};

/**
 * Safely remove item from localStorage
 */
export const safeRemoveItem = (key: string): void => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.removeItem(key);
    }
  } catch (error) {
    console.error(`Error removing localStorage item ${key}:`, error);
  }
};

/**
 * Clear all user-related data from localStorage
 */
export const clearUserData = (): void => {
  const keysToRemove = [
    'user_data',
    'profile_data',
    'auth_token',
    'city_details',
    'select_time',
    'bikeSearchData',
    'compareList',
    'booking_data',
    'user_preferences'
  ];

  keysToRemove.forEach(key => {
    safeRemoveItem(key);
  });
};

export const storage = {
  setCityDetails: ({ name, _id }: CityDetails) => {
    const cityDatastringified = JSON.stringify({
      name,
      _id,
    });
    localStorage.setItem(STORAGE_VAR.CITY_DETAILS, cityDatastringified);
  },
  clearCityDetails: () => {
    localStorage.removeItem(STORAGE_VAR.CITY_DETAILS);
  },

  getCityDetails: (): CityDetails => {
    if (typeof window !== "undefined" && window.localStorage) {
      const localValue = localStorage.getItem(STORAGE_VAR.CITY_DETAILS);
      let cityValues = {
        name: "",
        _id: "",
      } as CityDetails;
      if (localValue) {
        try {
          cityValues = JSON.parse(localValue);
        } catch {
          localStorage.removeItem(STORAGE_VAR.CITY_DETAILS);
        }
      }

      return {
        _id: cityValues?._id as string,
        name: cityValues?.name as string,
      };
    } else {
      return {
        _id: "",
        name: "",
      };
    }
  },

  setLoginDetails: async ({ phone, token }: LoginDetails) => {
    const loginDatastringified = JSON.stringify({
      phone,
      token,
    });
    localStorage.setItem(STORAGE_VAR.LOGIN_DETAILS, loginDatastringified);
    
    // Also set httpOnly cookies for SSR via API route
    try {
      await fetch('/api/auth/set-token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ phone, token }),
      });
    } catch (error) {
      console.error('Failed to set auth cookies:', error);
    }

    // Trigger auth change event for same-tab updates
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('authChange'));
    }
  },
  getLoginDetails: (): LoginDetails => {
    if (typeof window !== "undefined" && window.localStorage) {
      const localValue = localStorage.getItem(STORAGE_VAR.LOGIN_DETAILS);
      let phoneValues = {
        phone: "",
        token: "",
      } as LoginDetails;
      if (localValue) {
        try {
          phoneValues = JSON.parse(localValue);
        } catch {
          localStorage.removeItem(STORAGE_VAR.LOGIN_DETAILS);
        }
      }

      return {
        phone: phoneValues?.phone as string,
        token: phoneValues?.token as string,
      };
    } else {
      return {
        phone: "",
        token: "",
      };
    }
  },
  clearLoginDetails: async () => {
    localStorage.removeItem(STORAGE_VAR.LOGIN_DETAILS);
    
    // Also clear httpOnly cookies via API route
    try {
      await fetch('/api/auth/set-token', {
        method: 'DELETE',
      });
    } catch (error) {
      console.error('Failed to clear auth cookies:', error);
    }

    // Trigger auth change event for same-tab updates
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('authChange'));
    }
  },
};
